import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Avatar,
  IconButton,
  Tooltip,
  Collapse,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  ExpandLess,
  ExpandMore,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Visibility as VisibilityIcon,
  LocalHospital as LocalHospitalIcon,
  PointOfSale as PointOfSaleIcon,
  Medication as MedicationIcon,
  Analytics as AnalyticsIcon,
  Assessment as AssessmentIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  CalendarMonth as CalendarMonthIcon,
  RequestQuote as RequestQuoteIcon,
  ReceiptLong as ReceiptLongIcon,
  EventRepeat as EventRepeatIcon,
  Settings as SettingsIcon,
  MedicalServices as MedicalHistoryIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { hasPermission } from '../constants/permissions';

const SideNav = ({ open, onClose, variant = 'persistent' }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [expandedItems, setExpandedItems] = useState([]);

  const menuItems = [
    {
      text: 'Dashboard',
      icon: <DashboardIcon />,
      path: '/dashboard',
      perm: 'dashboard',
      roles: ['admin', 'reception', 'optometrist', 'doctor', 'opd'],
    },
    {
      text: 'Patients',
      icon: <PeopleIcon />,
      path: '/patients',
      perm: 'patients',
      roles: ['admin', 'reception', 'optometrist', 'doctor', 'opd'],
    },
    {
      text: 'Appointments',
      icon: <CalendarMonthIcon />,
      path: '/appointments',
      perm: 'appointments',
      roles: ['reception', 'admin'],
    },
    {
      text: 'Optometry',
      icon: <VisibilityIcon />,
      path: '/optometrist',
      perm: 'optometry',
      roles: ['optometrist', 'admin'],
    },
    {
      text: 'Doctor',
      icon: <LocalHospitalIcon />,
      path: '/doctor',
      perm: 'doctor',
      roles: ['doctor', 'admin'],
    },
    {
      text: 'OPD',
      icon: <PointOfSaleIcon />,
      path: '/opd',
      perm: 'opd',
      roles: ['opd', 'admin'],
    },
    {
      text: 'Billing & Payments',
      icon: <RequestQuoteIcon />,
      path: '/billing',
      perm: 'billing',
      roles: ['reception', 'opd', 'admin'],
    },
    {
      text: 'Follow-up Recall',
      icon: <EventRepeatIcon />,
      path: '/follow-ups',
      perm: 'followups',
      roles: ['doctor', 'reception', 'admin'],
    },
    {
      text: 'Medicines',
      icon: <MedicationIcon />,
      path: '/medicines',
      perm: 'medicines',
      roles: ['doctor', 'admin'],
    },
    {
      text: 'Analytics',
      icon: <AnalyticsIcon />,
      path: '/analytics',
      perm: 'analytics',
      roles: ['admin', 'doctor'],
    },
    {
      text: 'Reports',
      icon: <AssessmentIcon />,
      path: '/reports',
      perm: 'reports',
      roles: ['admin', 'doctor', 'reception', 'optometrist', 'opd'],
    },
    {
      text: 'Administration',
      icon: <AdminPanelSettingsIcon />,
      roles: ['admin'],
      children: [
        {
          text: 'User Management',
          icon: <AdminPanelSettingsIcon />,
          path: '/users',
          roles: ['admin'],
        },
        {
          text: 'Fee Master',
          icon: <ReceiptLongIcon />,
          path: '/fee-master',
          roles: ['admin'],
        },
        {
          text: 'Medical History',
          icon: <MedicalHistoryIcon />,
          path: '/medical-history',
          roles: ['admin'],
        },
        {
          text: 'Permission Matrix',
          icon: <AdminPanelSettingsIcon />,
          path: '/permissions',
          roles: ['admin'],
        },
        {
          text: 'Clinic Settings',
          icon: <SettingsIcon />,
          path: '/settings',
          roles: ['admin'],
        },
      ],
    },
  ];

  // Permission-driven visibility: if the item maps to a module key, the
  // permission list decides (allows custom grants beyond the role default).
  // Items without a perm key (Administration) stay role-gated.
  const itemAllowed = (item) =>
    item.perm ? hasPermission(user, item.perm) : item.roles.includes(user?.role);

  const filteredMenuItems = menuItems.filter((item) => {
    if (item.children) {
      return item.children.some(itemAllowed);
    }
    return itemAllowed(item);
  });

  const handleItemClick = (item) => {
    if (item.children) {
      handleToggleExpand(item.text);
    } else if (item.path) {
      navigate(item.path);
      if (isMobile) {
        onClose();
      }
    }
  };

  const handleChildClick = (childItem) => {
    navigate(childItem.path);
    if (isMobile) {
      onClose();
    }
  };

  const handleToggleExpand = (itemText) => {
    setExpandedItems((prev) =>
      prev.includes(itemText)
        ? prev.filter((item) => item !== itemText)
        : [...prev, itemText]
    );
  };

  const drawerWidth = open ? 280 : 80;

  const drawerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Logo Section */}
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: open ? 'flex-start' : 'center',
          minHeight: 64,
          borderBottom: '1px solid #E9E9F2',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: open ? 2 : 0,
          }}
        >
          <Avatar
            sx={{
              width: 40,
              height: 40,
              bgcolor: 'primary.main',
              fontSize: '1.25rem',
              fontWeight: 600,
            }}
          >
            ED
          </Avatar>
          {open && (
            <Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: '#3D3B54',
                  fontSize: '1.25rem',
                }}
              >
                EyeDealist
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: '#7B7A92', fontSize: '0.75rem' }}
              >
                Clinic Management
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* Navigation Menu */}
      <Box sx={{ flex: 1, py: 2 }}>
        <List sx={{ px: 1 }}>
          {filteredMenuItems.map((item) => {
            const isActive = location.pathname === item.path;
            const isExpanded = expandedItems.includes(item.text);

            return (
              <Box key={item.text}>
                <ListItem disablePadding sx={{ mb: 0.5 }}>
                  <Tooltip title={!open ? item.text : ''} placement="right">
                    <ListItemButton
                      onClick={() => handleItemClick(item)}
                      selected={isActive}
                      sx={{
                        borderRadius: '12px',
                        minHeight: 48,
                        px: 2,
                        mx: 1,
                        '&.Mui-selected': {
                          backgroundColor: '#EDEFFB',
                          color: '#626AC4',
                          '&:hover': {
                            backgroundColor: '#e0e7ff',
                          },
                          '& .MuiListItemIcon-root': {
                            color: '#626AC4',
                          },
                        },
                        '&:hover': {
                          backgroundColor: '#FAFAFD',
                        },
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: 40,
                          color: isActive ? '#626AC4' : '#7B7A92',
                          fontSize: '1.25rem',
                        }}
                      >
                        {item.icon}
                      </ListItemIcon>
                      {open && (
                        <>
                          <ListItemText
                            primary={item.text}
                            primaryTypographyProps={{
                              fontSize: '0.875rem',
                              fontWeight: isActive ? 600 : 500,
                              color: isActive ? '#626AC4' : '#56546E',
                            }}
                          />
                          {item.children && (
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleToggleExpand(item.text);
                              }}
                              sx={{
                                color: '#7B7A92',
                                '&:hover': {
                                  backgroundColor: 'transparent',
                                },
                              }}
                            >
                              {isExpanded ? <ExpandLess /> : <ExpandMore />}
                            </IconButton>
                          )}
                        </>
                      )}
                    </ListItemButton>
                  </Tooltip>
                </ListItem>

                {/* Submenu */}
                {item.children && open && (
                  <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                    <List sx={{ pl: 2 }}>
                      {item.children
                        .filter(itemAllowed)
                        .map((child) => {
                          const isChildActive = location.pathname === child.path;
                          return (
                            <ListItem key={child.text} disablePadding sx={{ mb: 0.5 }}>
                              <Tooltip title={!open ? child.text : ''} placement="right">
                                <ListItemButton
                                  onClick={() => handleChildClick(child)}
                                  selected={isChildActive}
                                  sx={{
                                    borderRadius: '8px',
                                    minHeight: 40,
                                    px: 2,
                                    mx: 1,
                                    '&.Mui-selected': {
                                      backgroundColor: '#F4F4F9',
                                      color: '#56546E',
                                      '&:hover': {
                                        backgroundColor: '#E9E9F2',
                                      },
                                      '& .MuiListItemIcon-root': {
                                        color: '#626AC4',
                                      },
                                    },
                                    '&:hover': {
                                      backgroundColor: '#FAFAFD',
                                    },
                                  }}
                                >
                                  <ListItemIcon
                                    sx={{
                                      minWidth: 32,
                                      color: isChildActive ? '#626AC4' : '#9B9AB0',
                                      fontSize: '1rem',
                                    }}
                                  >
                                    {child.icon}
                                  </ListItemIcon>
                                  {open && (
                                    <ListItemText
                                      primary={child.text}
                                      primaryTypographyProps={{
                                        fontSize: '0.8125rem',
                                        fontWeight: isChildActive ? 600 : 500,
                                        color: isChildActive ? '#56546E' : '#7B7A92',
                                      }}
                                    />
                                  )}
                                </ListItemButton>
                              </Tooltip>
                            </ListItem>
                          );
                        })}
                    </List>
                  </Collapse>
                )}
              </Box>
            );
          })}
        </List>
      </Box>

      {/* User Profile Section */}
      {open && user && (
        <Box sx={{ p: 2, borderTop: '1px solid #E9E9F2' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar
              sx={{
                width: 36,
                height: 36,
                bgcolor: 'primary.main',
                fontSize: '0.875rem',
                fontWeight: 600,
              }}
            >
              {user?.name?.charAt(0)}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: '#3D3B54',
                  fontSize: '0.875rem',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {user?.name}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: '#7B7A92',
                  fontSize: '0.75rem',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {user?.role}
              </Typography>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );

  return (
    <Drawer
      variant={variant}
      open={open}
      onClose={onClose}
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
          border: 'none',
          backgroundColor: '#ffffff',
          boxShadow: '0 0 20px rgba(0,0,0,0.1)',
          transition: 'width 0.3s ease-in-out',
          overflowX: 'hidden',
        },
      }}
    >
      {drawerContent}
    </Drawer>
  );
};

export default SideNav;
